import AccountInformation from './components/account-information';
import { ComboBox } from 'src/components/credit/ui/comboBox/ComboBox';
import DealerInformation from './components/dealer-information';
import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import { IUser } from 'src/utils/interfaces/user';
import { PERMISSIONS_TOKEN_NAME } from 'src/config';
import Prompt from 'src/components/ui/NavigationPrompt';
import TitleInformation from './components/title-information';
import TitleRequestInformation from './components/title-request-information';
import { TitleRequestService } from 'src/services/title-request.service';
import { useSelector } from 'react-redux';

import {
    buildTitleInquiryPayload,
    IBuildPayloadParams,
} from './utils/build-payload';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import React, { useCallback, useMemo, useState } from 'react';
import './styles.scss';

type AccountTypeOption = {
    value: string;
    label: string;
};

const TitleRequestPage: React.FC = () => {
    const accountTypeOptions: AccountTypeOption[] = useMemo(
        () => [
            { value: 'Retail', label: 'Retail' },
            { value: 'Lease', label: 'Lease' },
            { value: 'Service Loaner', label: 'Service Loaner' },
            { value: 'Company Car', label: 'Company Car' },
        ],
        [],
    );

    const isFieldUser = useSelector(
        (state: IUser) => state.accessUser ?? false,
    );

    const [formResetKey, setFormResetKey] = useState<number>(0); // eslint-disable-line no-magic-numbers
    const [accountType, setAccountType] = useState<string>('');
    const [accountTypeError, setAccountTypeError] = useState<boolean>(false);
    const [isDealerInformationSaved, setDealerInformationSaved] =
        useState<boolean>(false);
    const [dealerInformation, setDealerInformation] = useState<any>(null);
    const [isTitleRequestInformationSaved, setTitleRequestInformationSaved] =
        useState<boolean>(false);
    const [titleRequestInformation, setTitleRequestInformation] =
        useState<any>(null);
    const [isAccountInformationSaved, setAccountInformationSaved] =
        useState<boolean>(false);
    const [accountInformation, setAccountInformation] = useState<any>(null);
    const [isTitleInformationSaved, setTitleInformationSaved] =
        useState<boolean>(false);
    const [titleInformation, setTitleInformation] = useState<any>(null);
    const [isTitleRequestFormTouched, setTitleRequestFormTouched] =
        useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [showNotification, setShowNotification] = useState<boolean>(false);
    const [notificationMessage, setNotificationMessage] = useState<string>('');
    const [notificationType, setNotificationType] = useState<NotificationType>(
        NotificationType.SUCCESS,
    );

    const resetForm = useCallback(() => {
        setAccountType('');
        setAccountTypeError(false);
        setDealerInformationSaved(false);
        setDealerInformation(null);
        setTitleRequestInformationSaved(false);
        setTitleRequestInformation(null);
        setAccountInformationSaved(false);
        setAccountInformation(null);
        setTitleInformationSaved(false);
        setTitleInformation(null);
        setFormResetKey((prev) => prev + 1); // eslint-disable-line no-magic-numbers
    }, []);

    const handleFormSubmit = async () => {
        if (!accountType) {
            setAccountTypeError(true);
            return;
        }

        setIsSubmitting(true);
        setShowNotification(false);

        try {
            const permissionsToken =
                window.sessionStorage.getItem(PERMISSIONS_TOKEN_NAME) || '';

            const payloadParams: IBuildPayloadParams = {
                accountType,
                dealerInformation,
                titleRequestInformation,
                accountInformation,
                titleInformation,
                isFieldUser,
                permissionsToken,
            };

            const payload = buildTitleInquiryPayload(payloadParams);

            const service = new TitleRequestService();
            await service.submitTitleInquiry(
                payload,
                titleRequestInformation?.uploadFile,
            );

            setTitleRequestFormTouched(false);
            setNotificationType(NotificationType.SUCCESS);
            setNotificationMessage(
                'Your title request has been submitted successfully.',
            );
            setShowNotification(true);
            resetForm();
        } catch (error: any) {
            const backendMessage =
                error?.response?.data?.error?.message ||
                error?.response?.data?.message ||
                error?.message;
            setNotificationType(NotificationType.ERROR);
            setNotificationMessage(
                backendMessage ||
                    'We are unable to submit your request please try again',
            );
            setShowNotification(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className='title-request-wrapper-container dashboard-content-wrapper'>
            <Prompt
                show={isTitleRequestFormTouched}
                beforeUnload={true}
                titleContent={'Are you sure you want to exit?'}
                bodyContent={
                    'Your title request form is not submitted. If you exit now, you will lose all your data.'
                }
                cancelBtnLabel={'Cancel'}
                confirmBtnLabel={'Proceed'}
                onCancelHandler={() => {
                    // handle cancel
                }}
                onConfirmHandler={() => {
                    setTitleRequestFormTouched(false);
                }}
                isContentCentered={false}
                showButtons={true}
                icon={''}
                isBigModal={false}
                isError={false}
                progress={undefined}
                modalExtraClass='title-request-warning'
            />
            <div className='dashboard-content-container'>
                <section className='o-content-section'>
                    <div className='o-page-wrap'>
                        <header className='c-section-heading'>
                            <h3 className='c-section-heading__title'>
                                <span className='c-section-heading__title-text'>
                                    Title Request Form
                                </span>
                            </h3>
                        </header>

                        {showNotification && (
                            <Notification
                                content={notificationMessage}
                                type={notificationType}
                                visibility={showNotification}
                                setVisibility={setShowNotification}
                            />
                        )}

                        <div className='global-selector'>
                            <label
                                htmlFor='title-request-account-type'
                                className='c-form-field__label'
                            >
                                Account Type*
                            </label>
                            <ComboBox
                                name={'title-request-account-type'}
                                options={accountTypeOptions}
                                defaultValue={''}
                                placeholder={'Select Account Type'}
                                selectedOption={accountType}
                                onSelect={(type) => {
                                    const selectedRequestType =
                                        titleRequestInformation?.documentRequestType;
                                    const selectionInvalidForNewAccountType =
                                        type === 'Lease'
                                            ? selectedRequestType ===
                                              'Power of Attorney (POA)'
                                            : selectedRequestType?.includes(
                                                  '(POA)',
                                              ) &&
                                              selectedRequestType !==
                                                  'Power of Attorney (POA)';

                                    if (
                                        type !== accountType &&
                                        selectionInvalidForNewAccountType
                                    ) {
                                        setTitleRequestInformationSaved(false);
                                        setTitleRequestInformation(null);
                                    }
                                    setAccountType(type);
                                    setAccountTypeError(false);
                                    setTitleRequestFormTouched(true);
                                }}
                                disabled={false}
                            />
                            {accountTypeError && (
                                <span className='c-error-message'>
                                    Account type is required.
                                </span>
                            )}
                        </div>

                        <DealerInformation
                            key={`dealer-${formResetKey}`}
                            onFormTouched={() => {
                                setTitleRequestFormTouched(true);
                            }}
                            onSave={(dealerInfo) => {
                                setDealerInformation(dealerInfo);
                                setDealerInformationSaved(true);
                                setTitleRequestFormTouched(true);
                            }}
                        />

                        <TitleRequestInformation
                            key={`request-${formResetKey}`}
                            accountType={accountType}
                            isEnabled={isDealerInformationSaved}
                            onFormTouched={() => {
                                setTitleRequestFormTouched(true);
                            }}
                            onSave={(requestInfo) => {
                                setTitleRequestInformation(requestInfo);
                                setTitleRequestInformationSaved(true);
                                setTitleRequestFormTouched(true);
                            }}
                        />

                        <AccountInformation
                            key={`account-${formResetKey}`}
                            isEnabled={isTitleRequestInformationSaved}
                            onFormTouched={() => {
                                setTitleRequestFormTouched(true);
                            }}
                            onSave={(accountInfo) => {
                                setAccountInformation(accountInfo);
                                setAccountInformationSaved(true);
                                setTitleRequestFormTouched(true);
                            }}
                        />

                        <TitleInformation
                            key={`title-${formResetKey}`}
                            isEnabled={isAccountInformationSaved}
                            onFormTouched={() => {
                                setTitleRequestFormTouched(true);
                            }}
                            onSave={(titleInfo) => {
                                setTitleInformation(titleInfo);
                                setTitleInformationSaved(true);
                                setTitleRequestFormTouched(true);
                            }}
                        />

                        <div className='o-fieldset__row action-wrapper'>
                            <button
                                className='c-btn'
                                type='button'
                                disabled={
                                    !isDealerInformationSaved ||
                                    !isTitleRequestInformationSaved ||
                                    !isAccountInformationSaved ||
                                    !isTitleInformationSaved ||
                                    isSubmitting
                                }
                                onClick={handleFormSubmit}
                            >
                                <span className='c-btn__text'>
                                    {isSubmitting ? 'Submitting...' : 'Submit'}
                                </span>
                            </button>
                        </div>

                        <div className='privacy-footer-container'>
                            <FooterPrivacy />
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default TitleRequestPage;
